import { useEffect, useRef, useState } from "react";
import type { CreditStatus, Project, User } from "../api/types";
import { creditLevel, creditText, creditTip } from "../lib/credits";
import { playCue, setSoundOn, soundOn, unlockAudio } from "../lib/sound";
import { ACCOUNT_ITEMS, PROJECT_ITEMS, hint } from "../menus";
import type { Theme } from "../theme/theme";
import { ActionMenu, type MenuState } from "./ActionMenu";
import { Icon } from "./icons";

/**
 * The bar across the top: the product (home), the project you are in with a switcher and its tools, then credits,
 * shortcuts, the theme and your account. Replaces the desktop's menu bar, toolbar and status bar.
 */
export function TopBar({ user, theme, credits, projects, project, showProject, menuState, onAction, onSwitch, onNew, canCreate }: {
  user: User | null; theme: Theme; credits: CreditStatus | null;
  projects: Project[]; project: Project | null; showProject: boolean;
  menuState: MenuState; onAction: (action: string) => void; onSwitch: (p: Project | null) => void;
  onNew: () => void; canCreate: boolean;
}) {
  const creditLabel = creditText(credits);
  const other = theme === "dark" ? "light" : "dark";
  const [sound, setSound] = useState(soundOn);
  useEffect(() => { unlockAudio(); }, []);
  const toggleSound = () => {
    const next = !sound;
    setSoundOn(next); setSound(next);
    if (next) playCue("ready", true);          // so turning it on is heard, and the browser is allowed to play from then on
  };
  return (
    <header className="topbar" role="banner">
      <button className="brand" onClick={() => onSwitch(null)} data-testid="tb-dashboard" title="All projects">
        <img src="/brand/logo.png" width={24} height={24} alt="" />
        <span>Model Bench</span>
      </button>
      {showProject && (<>
        <span className="crumb-sep" aria-hidden="true">/</span>
        <ProjectSwitcher projects={projects} project={project} onSwitch={onSwitch} onNew={onNew} canCreate={canCreate} />
        <ActionMenu label={<Icon name="more" />} items={PROJECT_ITEMS()} state={menuState} onAction={onAction}
          testId="menu-project" className="btn ghost icon-btn" title="Project tools" />
      </>)}
      <span className="spacer" />
      {creditLabel && credits && (
        <span className={`credits credits-${creditLevel(credits)}`} title={creditTip(credits)} data-testid="tb-credits"><Icon name="credit" size={14} />{creditLabel}</span>
      )}
      <button className="btn ghost icon-btn" onClick={() => onAction("help.shortcuts")} data-testid="tb-shortcuts"
        title={`Keyboard shortcuts  (${hint("help.shortcuts")})`} aria-label="Keyboard shortcuts"><Icon name="keyboard" /></button>
      <button className="btn ghost icon-btn" onClick={toggleSound} data-testid="tb-sound" aria-pressed={sound}
        title={sound ? "Sounds on: plays when training or an export finishes. Click to turn off." : "Sounds off. Click to turn on."}
        aria-label={sound ? "Turn sounds off" : "Turn sounds on"}><Icon name={sound ? "volume" : "volumeOff"} /></button>
      <button className="btn ghost icon-btn" onClick={() => onAction("theme.toggle")} data-testid="tb-theme"
        title={`Switch to ${other} theme  (${hint("app.theme")})`} aria-label={`Switch to ${other} theme`}>
        <Icon name={theme === "dark" ? "sun" : "moon"} />
      </button>
      <ActionMenu items={ACCOUNT_ITEMS()} state={menuState} onAction={onAction} testId="menu-account" className="account" align="right"
        label={<>
          <span className="avatar" aria-hidden="true">{(user?.username ?? "?").slice(0, 1).toUpperCase()}</span>
          <span className="account-name" data-testid="tb-user">{user?.username}<span className="role">{user?.role}</span></span>
          <Icon name="chevronDown" size={14} />
        </>}
        header={<div className="menu-header"><b>{user?.username}</b><span className="muted">{user?.email}</span></div>} />
    </header>
  );
}

/** The project name in the top bar; opens a list of the organization's projects to switch to. */
function ProjectSwitcher({ projects, project, onSwitch, onNew, canCreate }: {
  projects: Project[]; project: Project | null; onSwitch: (p: Project | null) => void; onNew: () => void; canCreate: boolean;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    root.current?.querySelector<HTMLButtonElement>('[role="menu"] [aria-current="true"], [role="menu"] button')?.focus();
    const away = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    const keys = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); setOpen(false); return; }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const buttons = [...(root.current?.querySelectorAll<HTMLButtonElement>('[role="menu"] button') ?? [])];
      if (!buttons.length) return;
      e.preventDefault(); e.stopPropagation();
      const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
      buttons[(at + (e.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length].focus();
    };
    window.addEventListener("mousedown", away);
    window.addEventListener("keydown", keys, true);
    return () => { window.removeEventListener("mousedown", away); window.removeEventListener("keydown", keys, true); };
  }, [open]);
  return (
    <div className="action-menu" ref={root}>
      <button className="btn ghost switcher" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)} data-testid="project-switcher">
        <span className="switcher-name">{project?.name ?? "Select a project"}</span><Icon name="chevronDown" size={14} />
      </button>
      {open && (
        <div className="menu menu-left switcher-menu" role="menu">
          <div className="menu-caption">Projects</div>
          {projects.length === 0 && <div className="menu-empty muted">No projects yet.</div>}
          {projects.map((p) => (
            <button key={p.id} className="menu-item" role="menuitemradio" aria-checked={p.id === project?.id} aria-current={p.id === project?.id}
              data-testid={`switch-${p.name}`} onClick={() => { setOpen(false); onSwitch(p); }}>
              <span>{p.name}</span><span className="key">{p.id === project?.id ? "✓" : ""}</span>
            </button>
          ))}
          <div className="menu-sep" role="separator" />
          <button className="menu-item" role="menuitem" data-testid="switch-all" onClick={() => { setOpen(false); onSwitch(null); }}>
            <span>All projects</span><span className="key" />
          </button>
          {canCreate && (
            <button className="menu-item" role="menuitem" data-testid="switch-new" onClick={() => { setOpen(false); onNew(); }}>
              <span>New project…</span><span className="key" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
